import PageHeader from "../components/PageHeader";
import ConfigTabs from "../components/ConfigTabs";
import useSettings from "../hooks/useSettings";
import { Save, ShieldAlert } from "lucide-react";

export default function ConfigRisk() {
  const { form, set, save, loading } = useSettings();
  if (!form) return <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500">Loading...</div>;

  const num = (k) => (e) => set(k, parseFloat(e.target.value) || 0);
  const int = (k) => (e) => set(k, parseInt(e.target.value) || 0);

  return (
    <div data-testid="config-risk-page">
      <PageHeader title="Configuration" subtitle="Risk Management · thresholds, KYC triggers and auto-approval rules" />
      <ConfigTabs />

      <form onSubmit={(e) => { e.preventDefault(); save(); }} className="space-y-5 max-w-5xl" data-testid="config-risk-form">
        <Section title="Deposit Limits (৳ BDT)" icon={ShieldAlert}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Minimum Deposit"><input type="number" step="0.01" value={form.min_deposit} onChange={num("min_deposit")} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" data-testid="cfg-min-deposit" /></Field>
            <Field label="Maximum Deposit"><input type="number" step="0.01" value={form.max_deposit} onChange={num("max_deposit")} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" /></Field>
            <Field label="Auto-approve Below"><input type="number" step="0.01" value={form.auto_approve_deposit_below} onChange={num("auto_approve_deposit_below")} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" data-testid="cfg-auto-approve" /></Field>
          </div>
          <p className="text-xs text-gray-500 mt-2">Deposits below auto-approve threshold are accepted without manual review.</p>
        </Section>

        <Section title="Withdrawal Limits (৳ BDT)" icon={ShieldAlert}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Minimum Withdraw"><input type="number" step="0.01" value={form.min_withdraw} onChange={num("min_withdraw")} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" /></Field>
            <Field label="Maximum Withdraw"><input type="number" step="0.01" value={form.max_withdraw} onChange={num("max_withdraw")} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" /></Field>
            <Field label="Max Withdrawals / Day"><input type="number" value={form.max_withdrawals_per_day} onChange={int("max_withdrawals_per_day")} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" data-testid="cfg-max-wd-per-day" /></Field>
          </div>
        </Section>

        <Section title="Per-Channel Deposit Limits (৳ BDT)" icon={ShieldAlert}>
          <p className="text-xs text-gray-500 mb-3">Override global deposit limits per channel. Set to <strong>0</strong> to fall back to the global value above. Stricter (smaller) of channel vs gateway-config wins.</p>
          <div className="space-y-3">
            <ChannelRow label="Agent" testPrefix="agent-dep" minKey="agent_min_deposit" maxKey="agent_max_deposit" form={form} num={num} />
            <ChannelRow label="Crypto" testPrefix="crypto-dep" minKey="crypto_min_deposit" maxKey="crypto_max_deposit" form={form} num={num} />
            <ChannelRow label="E-Wallet (bKash · Nagad · Rocket · Upay)" testPrefix="ewallet-dep" minKey="ewallet_min_deposit" maxKey="ewallet_max_deposit" form={form} num={num} />
          </div>
        </Section>

        <Section title="Per-Channel Withdrawal Limits (৳ BDT)" icon={ShieldAlert}>
          <p className="text-xs text-gray-500 mb-3">Override global withdrawal limits per channel. Set to <strong>0</strong> to fall back to the global value above.</p>
          <div className="space-y-3">
            <ChannelRow label="Agent" testPrefix="agent-wd" minKey="agent_min_withdraw" maxKey="agent_max_withdraw" form={form} num={num} />
            <ChannelRow label="Crypto" testPrefix="crypto-wd" minKey="crypto_min_withdraw" maxKey="crypto_max_withdraw" form={form} num={num} />
            <ChannelRow label="E-Wallet (bKash · Nagad · Rocket · Upay)" testPrefix="ewallet-wd" minKey="ewallet_min_withdraw" maxKey="ewallet_max_withdraw" form={form} num={num} />
          </div>
        </Section>

        <Section title="Bonus Withdrawal Lock" icon={ShieldAlert}>
          <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50">
            <input
              type="checkbox"
              checked={form.block_bonus_withdraw_until_first_deposit !== false}
              onChange={(e) => set("block_bonus_withdraw_until_first_deposit", e.target.checked)}
              data-testid="cfg-block-bonus-withdraw"
              className="mt-0.5"
            />
            <div>
              <div className="text-sm font-semibold text-gray-900">Block bonus withdrawal until first deposit</div>
              <div className="text-xs text-gray-500 mt-0.5">When ON, players who have only received bonuses (registration bonus, referral, free-spin, etc.) cannot withdraw until they complete at least one successful deposit. Strongly recommended to prevent bonus-abuse fraud.</div>
            </div>
          </label>
        </Section>

        <Section title="Bet & Exposure Limits (৳ BDT)" icon={ShieldAlert}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Max Single Bet"><input type="number" step="0.01" value={form.max_bet_amount} onChange={num("max_bet_amount")} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" data-testid="cfg-max-bet" /></Field>
            <Field label="Max Daily Loss"><input type="number" step="0.01" value={form.max_daily_loss} onChange={num("max_daily_loss")} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" /></Field>
          </div>
        </Section>

        <Section title="KYC & Compliance (৳ BDT)" icon={ShieldAlert}>
          <Field label="KYC Required Above">
            <input type="number" step="0.01" value={form.kyc_required_above} onChange={num("kyc_required_above")} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" data-testid="cfg-kyc-threshold" />
          </Field>
          <p className="text-xs text-gray-500 mt-2">Players with cumulative deposits above this value must complete KYC verification before withdrawal.</p>
        </Section>

        <Section title="Verification Gates (Email / Phone / KYC)" icon={ShieldAlert}>
          <p className="text-xs text-gray-500 mb-3">Globally enable or disable each verification step for site users. When OFF, the corresponding check is skipped during withdrawal and other gated actions — letting users transact without verifying that channel. Per-user override is available from <a href="/root/admin/players" className="text-emerald-600 hover:underline">Player Management → Edit Player → Verification</a>.</p>
          <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50 mb-2">
            <input
              type="checkbox"
              checked={form.require_email_verification !== false}
              onChange={(e) => set("require_email_verification", e.target.checked)}
              data-testid="cfg-require-email-verification"
            />
            <div>
              <div className="text-sm font-semibold text-gray-900">Require Email Verification</div>
              <div className="text-xs text-gray-500">When ON, users must verify their email (or phone) before withdrawing.</div>
            </div>
          </label>
          <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50 mb-2">
            <input
              type="checkbox"
              checked={form.require_phone_verification !== false}
              onChange={(e) => set("require_phone_verification", e.target.checked)}
              data-testid="cfg-require-phone-verification"
            />
            <div>
              <div className="text-sm font-semibold text-gray-900">Require Phone Verification</div>
              <div className="text-xs text-gray-500">When ON, phone verification counts as a valid verification channel for withdrawal. Turning BOTH email & phone OFF skips the email/phone gate entirely.</div>
            </div>
          </label>
          <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50">
            <input
              type="checkbox"
              checked={form.require_kyc_verification !== false}
              onChange={(e) => set("require_kyc_verification", e.target.checked)}
              data-testid="cfg-require-kyc-verification"
            />
            <div>
              <div className="text-sm font-semibold text-gray-900">Require KYC Verification (high balance)</div>
              <div className="text-xs text-gray-500">When ON, players whose balance exceeds the KYC threshold above must complete KYC before withdrawal. Turn OFF to skip KYC for everyone.</div>
            </div>
          </label>
        </Section>

        <Section title="KYC Completion Bonus" icon={ShieldAlert}>
          <p className="text-xs text-gray-500 mb-3">Auto-credit a bonus to a player&apos;s wallet the first time their KYC becomes <b>Verified</b> (admin approval, AI auto-approval, or admin override). Idempotent — credited only once per user.</p>
          <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50 mb-3">
            <input
              type="checkbox"
              checked={!!form.kyc_bonus_enabled}
              onChange={(e) => set("kyc_bonus_enabled", e.target.checked)}
              data-testid="cfg-kyc-bonus-enabled"
            />
            <div>
              <div className="text-sm font-semibold text-gray-900">Enable KYC Completion Bonus</div>
              <div className="text-xs text-gray-500">Players receive the bonus amount (below) credited to their wallet immediately after KYC is approved, plus a notification.</div>
            </div>
          </label>
          <Field label="KYC Bonus Amount (৳ BDT)">
            <input
              type="number"
              step="1"
              min="0"
              value={form.kyc_bonus_amount ?? 100}
              onChange={num("kyc_bonus_amount")}
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular"
              data-testid="cfg-kyc-bonus-amount"
            />
          </Field>
        </Section>

        <Section title="Bonus Limits (৳ BDT)" icon={ShieldAlert}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Registration Bonus"><input type="number" step="0.01" value={form.registration_bonus} onChange={num("registration_bonus")} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" data-testid="cfg-registration-bonus" /></Field>
            <Field label="Referral Bonus (legacy flat, unused)"><input type="number" step="0.01" value={form.referral_bonus} onChange={num("referral_bonus")} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" /></Field>
          </div>
          <label className="mt-4 flex items-center gap-3 p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50">
            <input
              type="checkbox"
              checked={form.registration_bonus_enabled !== false}
              onChange={(e) => set("registration_bonus_enabled", e.target.checked)}
              data-testid="cfg-registration-bonus-enabled"
            />
            <div>
              <div className="text-sm font-semibold text-gray-900">Enable Registration Bonus</div>
              <div className="text-xs text-gray-500">When ON, every new user who registers receives the bonus amount (above) as starting balance and a welcome notification.</div>
            </div>
          </label>
        </Section>

        <Section title="Quick-Signup Recovery Bonus (৳ BDT)" icon={ShieldAlert}>
          <p className="text-xs text-gray-500 mb-3">
            One-time bonus credited when a <strong>one-click / quick-signup user</strong> links a phone number OR email address for the first time.
            The banner shown on their dashboard automatically displays the amount set here.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Recovery Bonus Amount">
              <input
                type="number"
                step="1"
                min="0"
                value={form.quick_signup_recovery_bonus_amount ?? 10}
                onChange={num("quick_signup_recovery_bonus_amount")}
                className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular"
                data-testid="cfg-recovery-bonus-amount"
              />
            </Field>
          </div>
          <label className="mt-4 flex items-center gap-3 p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50">
            <input
              type="checkbox"
              checked={form.quick_signup_recovery_bonus_enabled !== false}
              onChange={(e) => set("quick_signup_recovery_bonus_enabled", e.target.checked)}
              data-testid="cfg-recovery-bonus-enabled"
            />
            <div>
              <div className="text-sm font-semibold text-gray-900">Enable Quick-Signup Recovery Bonus</div>
              <div className="text-xs text-gray-500">
                When ON, quick-signup users are credited this amount instantly the first time they verify a phone OR email.
                Awarded once per account. A push notification is sent immediately to the user in-app.
              </div>
            </div>
          </label>
        </Section>

        <Section title="Deposit Bonus (% of each deposit)" icon={ShieldAlert}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Deposit Bonus %">
              <input type="number" step="0.1" min="0" max="100" value={form.deposit_bonus_pct ?? 10} onChange={num("deposit_bonus_pct")} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" data-testid="cfg-deposit-bonus-pct" />
            </Field>
          </div>
          <label className="mt-4 flex items-center gap-3 p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50">
            <input
              type="checkbox"
              checked={form.deposit_bonus_enabled !== false}
              onChange={(e) => set("deposit_bonus_enabled", e.target.checked)}
              data-testid="cfg-deposit-bonus-enabled"
            />
            <div>
              <div className="text-sm font-semibold text-gray-900">Enable Deposit Bonus</div>
              <div className="text-xs text-gray-500">When ON, every successful deposit automatically credits an extra % bonus to the user&apos;s balance (e.g. 10% on ৳1000 = +৳100 bonus) along with a notification.</div>
            </div>
          </label>
        </Section>

        <Section title="Referral Bonus (% commission on referred user's bets)" icon={ShieldAlert}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Referral Commission %">
              <input type="number" step="0.1" min="0" max="100" value={form.referral_bonus_pct ?? 10} onChange={num("referral_bonus_pct")} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" data-testid="cfg-referral-bonus-pct" />
            </Field>
          </div>
          <label className="mt-4 flex items-center gap-3 p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50">
            <input
              type="checkbox"
              checked={form.referral_bonus_enabled !== false}
              onChange={(e) => set("referral_bonus_enabled", e.target.checked)}
              data-testid="cfg-referral-bonus-enabled"
            />
            <div>
              <div className="text-sm font-semibold text-gray-900">Enable Referral Commission</div>
              <div className="text-xs text-gray-500">When ON, every time a referred user places a bet, the referrer automatically earns this % of the stake as commission (credited to their balance + notification). New signups with a valid refer code are tracked; the referrer also gets an instant &quot;New referral signup&quot; alert.</div>
            </div>
          </label>
        </Section>

        <Section title="Turnover / Wagering Requirement" icon={ShieldAlert}>
          <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50 mb-4">
            <input
              type="checkbox"
              checked={form.turnover_enabled !== false}
              onChange={(e) => set("turnover_enabled", e.target.checked)}
              data-testid="cfg-turnover-enabled"
            />
            <div>
              <div className="text-sm font-semibold text-gray-900">Enable Turnover Requirement</div>
              <div className="text-xs text-gray-500">When ON, users must wager (bet) a multiple of each bonus amount before they can withdraw. Withdrawals are blocked with a clear message until the requirement is met.</div>
            </div>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Registration Bonus Multiplier (×)">
              <input type="number" step="0.1" min="0" value={form.turnover_registration_multiplier ?? 1} onChange={num("turnover_registration_multiplier")} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" data-testid="cfg-turnover-reg-mult" />
            </Field>
            <Field label="Deposit Bonus Multiplier (×)">
              <input type="number" step="0.1" min="0" value={form.turnover_deposit_multiplier ?? 1} onChange={num("turnover_deposit_multiplier")} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" data-testid="cfg-turnover-dep-mult" />
            </Field>
            <Field label="Referral Commission Multiplier (×)">
              <input type="number" step="0.1" min="0" value={form.turnover_referral_multiplier ?? 1} onChange={num("turnover_referral_multiplier")} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" data-testid="cfg-turnover-ref-mult" />
            </Field>
          </div>
          <p className="text-xs text-gray-500 mt-2">Example: 1× means ৳50 registration bonus requires ৳50 wagered; 3× means ৳50 requires ৳150. Live monitoring at <a href="/root/admin/turnover" className="text-emerald-600 hover:underline">Turnover Management</a>.</p>
        </Section>

        <div className="flex justify-end">
          <button type="submit" disabled={loading} className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50" data-testid="cfg-risk-save">
            <Save size={14} /> {loading ? "Saving..." : "Save Risk Settings"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (<div><label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">{label}</label>{children}</div>);
}
function ChannelRow({ label, minKey, maxKey, form, num, testPrefix }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end p-3 border border-gray-200 rounded-sm bg-gray-50/50">
      <div className="text-sm font-semibold text-gray-900">{label}</div>
      <Field label={`Min (${minKey.includes("deposit") ? "Deposit" : "Withdraw"})`}>
        <input type="number" step="1" min="0" value={form[minKey] ?? 0} onChange={num(minKey)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular bg-white" data-testid={`cfg-${testPrefix}-min`} />
      </Field>
      <Field label={`Max (${maxKey.includes("deposit") ? "Deposit" : "Withdraw"})`}>
        <input type="number" step="1" min="0" value={form[maxKey] ?? 0} onChange={num(maxKey)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular bg-white" data-testid={`cfg-${testPrefix}-max`} />
      </Field>
    </div>
  );
}
function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-sm p-5">
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
        {Icon && <Icon size={14} className="text-amber-500" />}
        <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">{title}</div>
      </div>
      {children}
    </div>
  );
}
